/**
 * Enum for feature types.
 */
export enum FeatureType {
  BOOLEAN = 'boolean',
  DROPDOWN = 'dropdown'
}

/**
 * Interface for feature options.
 */
export interface FeatureOption {
  value: string;
  label: string;
}

/**
 * Interface for feature configuration.
 */
export interface FeatureConfig {
  apiName: string;
  title: string;
  description: string;
  type: FeatureType;
  options?: FeatureOption[];
  editable?: boolean;
}

/**
 * Array of feature configurations. These are all features that are exposed to the tenant in the UI. 
 * We're maintaining the labels and description here to accomadate different languages if applicable.
 * Ultimately, the DACO API is the source of truth for the feature settings. For security, whether a setting is editable or not is 
 * determined by the DACO API.
 * 
 * @type {FeatureConfig[]}
 */
export const featuresConfig: FeatureConfig[] = [
  {
    apiName: 'CAULDRON_BULK_JOBS_ENABLED',
    title: 'Advanced Jobs Enabled',
    description: 'If enabled, job-based syncs (on-demand, cleanup and manual) sync use the latest sync technology.',
    type: FeatureType.BOOLEAN,
    editable: false
  },
  {
    apiName: 'CAULDRON_DELETE_SUBSCRIPTION_FIRST',
    title: 'Subscription Pre-Delete',
    description: 'If enabled, subscriptions and related objects are deleted and recreated instead of upserted. This is also known as pre-delete. Originally implemented for backwards compatibility with legacy product and is not recommended.',
    type: FeatureType.BOOLEAN,
    editable: false
  },
  {
    apiName: 'CAULDRON_REFUND_CALCULATE_PAYMENT_METHOD',
    title: 'Refund Payment Method Calculation',
    description: 'If enabled, this calculates the Refund.PaymentMethod__c field.',
    type: FeatureType.BOOLEAN,
    editable: true
  },
  {
    apiName: 'DISABLE_SYNC_OF_DELETED_PRODUCTS',
    title: 'Hard Delete Products in SFDC',
    description: 'If true, products that are deleted in Zuora will be hard-deleted. If false: Products will soft delete if deleted in Zuora.',
    type: FeatureType.BOOLEAN,
    editable: true
  },
  {
    apiName: 'HIERARCHICAL_SYNC_ON_CRM_ID_CHANGE',
    title: 'Heirarchical Resync on CRM ID Change',
    description: 'If enabled, it lines up a resync of child records when an account CRM ID change is detected.',
    type: FeatureType.BOOLEAN,
    editable: true
  },
  {
    apiName: 'IGNORE_NULL_VALUES_FOR_CUSTOM_FIELDS',
    title: 'Ignore Custom Field Null Values',
    description: 'If enabled, null values for custom fields are dropped. This means that if a custom field has an existing value in SFDC and is set to null in Zuora, the field will retain historical data in SFDC.',
    type: FeatureType.BOOLEAN,
    editable: true
  },
  {
    apiName: 'MAX_SYNC_ATTEMPTS',
    title: 'Number of Sync Attempts Before Marked as Error',
    description: 'The number of sync attempts for retryable errors before the system gives up and writes the record to the failure list.',
    type: FeatureType.DROPDOWN,
    options: [
      { value: '1', label: '1 attempt' },
      { value: '2', label: '2 attempts' },
      { value: '3', label: '3 attempts' },
      { value: '5', label: '5 attempts' }
    ],
    editable: false
  },
  {
    apiName: 'ONLY_SYNC_POSTED_INVOICE',
    title: 'Only Sync Posted Invoices',
    description: 'If enabled, only invoices with a Posted status will be synced. If an invoice\'s status changes from Posted to Draft or Cancelled, it will be deleted. If disabled, all invoices will be synced regardless of status.',
    type: FeatureType.BOOLEAN,
    editable: true
  },
  {
    apiName: 'RECORD_BATCH_SIZE',
    title: 'Max SOAP Batch Size',
    description: 'The batch size for SFDC SOAP upserts. The maximum value is 200. Setting it lower is not recommended as it can impact SFDC API usage.',
    type: FeatureType.DROPDOWN,
    options: [
      { value: '50', label: '50 records' },
      { value: '100', label: '100 records' },
      { value: '200', label: '200 records' }
    ],
    editable: true
  },
  {
    apiName: 'SFDC_READ_TIMEOUT_MS',
    title: 'SFDC Read Time-Out Threshold',
    description: 'The value in milliseconds for all SFDC SOAP requests, not just reads. It\'s not recommended to set it higher than 60 seconds as it can impact overall queue latency.',
    type: FeatureType.DROPDOWN,
    options: [
      { value: '30000', label: '30000 milliseconds' },
      { value: '45000', label: '45000 milliseconds' },
      { value: '60000', label: '60000 milliseconds' }
    ],
    editable: true
  },
  {
    apiName: 'SKIP_REFUND_PAYMENT_METHOD_FIELD',
    title: 'Skip Refund Payment Method Fields',
    description: 'If enabled, it skips syncing Refund.PaymentMethod__c and Refund.PaymentMethodId__c fields.',
    type: FeatureType.BOOLEAN,
    editable: true
  },
  {
    apiName: 'SKIP_SCHEDULED_RETRY',
    title: 'Skip Scheduled Retry',
    description: 'If enabled, the 12-hour automatic failure list retry will be skipped.',
    type: FeatureType.BOOLEAN,
    editable: true
  },
  {
    apiName: 'SYNC_CANCELED_ACCOUNT_DATA',
    title: 'Sync Cancelled Accounts',
    description: 'If enabled, accounts with a Cancelled status will be synced. If disabled, cancelled accounts will be deleted from SFDC.',
    type: FeatureType.BOOLEAN,
    editable: true
  },
  {
    apiName: 'USE_PAYMENT_METHOD_CALCULATOR',
    title: 'Use Payment Method Calculator',
    description: 'If enabled, it calculates Payment.PaymentMethod__c to contain a formatted string.',
    type: FeatureType.BOOLEAN,
    editable: true
  },
  {
    apiName: 'USE_RECURRING_PERIOD_CALCULATOR',
    title: 'Use Recurring Period Calculator',
    description: 'If enabled, it calculates ProductRatePlanCharge.RecurringPeriod__c to be a formatted string.',
    type: FeatureType.BOOLEAN,
    editable: true
  }
];
